import { supabase } from '../lib/supabase';

async function authedFetch(url, options) {
  const { data: { session } } = await supabase.auth.getSession();
  const token = session?.access_token;

  const response = await fetch(url, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
  });

  const body = await response.json();

  if (!response.ok) {
    throw new Error(body.error || 'Request failed');
  }

  return body.data;
}

/**
 * Update a session with admin privileges (via the server-side /api/admin/sessions function).
 * @param {number} sessionId - The session ID
 * @param {Object} updates - The updates to apply
 * @returns {Promise<Object>} Updated session data
 */
export const updateSessionAdmin = (sessionId, updates) =>
  authedFetch('/api/admin/sessions', {
    method: 'PUT',
    body: JSON.stringify({ sessionId, updates }),
  });

/**
 * Delete a session with admin privileges.
 * @param {number} sessionId - The session ID
 * @returns {Promise<boolean>} Success status
 */
export const deleteSessionAdmin = async (sessionId) => {
  await authedFetch('/api/admin/sessions', {
    method: 'DELETE',
    body: JSON.stringify({ sessionId }),
  });
  return true;
};

/**
 * Delete multiple sessions with admin privileges.
 * @param {Array<number>} sessionIds - Array of session IDs
 * @returns {Promise<boolean>} Success status
 */
export const deleteSessionsAdmin = async (sessionIds) => {
  await authedFetch('/api/admin/sessions', {
    method: 'DELETE',
    body: JSON.stringify({ sessionIds }),
  });
  return true;
};

/**
 * Create a new session with admin privileges.
 * @param {Object} sessionData - The session data
 * @returns {Promise<Object>} Created session data
 */
export const createSessionAdmin = (sessionData) =>
  authedFetch('/api/admin/sessions', {
    method: 'POST',
    body: JSON.stringify(sessionData),
  });

/**
 * Get all admin users with their auth status.
 * @returns {Promise<Array>} List of admin users
 */
export const getAdminUsers = () =>
  authedFetch('/api/admin/users', { method: 'GET' });

/**
 * Delete an admin user.
 * @param {string} email - The user email to delete
 * @returns {Promise<Object>} Delete results
 */
export const deleteAdminUser = (email) =>
  authedFetch('/api/admin/users', {
    method: 'DELETE',
    body: JSON.stringify({ email }),
  });

/**
 * Delete multiple admin users.
 * @param {Array<string>} emails - Array of user emails to delete
 * @returns {Promise<Object>} Results with success/failure counts
 */
export const deleteAdminUsers = (emails) =>
  authedFetch('/api/admin/users', {
    method: 'DELETE',
    body: JSON.stringify({ emails }),
  });

/**
 * Create a new admin user (auth.users + admin_users, handled server-side).
 * @param {Object} userData - User data {email, password, name}
 * @returns {Promise<Object>} Created user data
 */
export const createAdminUser = (userData) =>
  authedFetch('/api/admin/users', {
    method: 'POST',
    body: JSON.stringify(userData),
  });

export default {
  updateSessionAdmin,
  deleteSessionAdmin,
  deleteSessionsAdmin,
  createSessionAdmin,
  getAdminUsers,
  deleteAdminUser,
  deleteAdminUsers,
  createAdminUser,
};
